"use client"

import type React from "react"
import { createContext, useState, useContext, useEffect } from "react"
import type { ReactNode } from "react"

// Define language type
type Language = {
  code: string
  name: string
  flag: string
}

// Define languages object type
type LanguageMap = {
  [key: string]: Language
}

// Define context value type
type LanguageContextType = {
  language: Language
  changeLanguage: (code: string) => void
  languages: LanguageMap
  translations: typeof translations
}

// Available languages
export const LANGUAGES: LanguageMap = {
  EN: { code: "en", name: "English", flag: "🇬🇧" },
  NL: { code: "nl", name: "Nederlands", flag: "🇳🇱" },
}

// Create the context with initial undefined value
const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

// Props for the provider component
interface LanguageProviderProps {
  children: ReactNode
}

// Translations object
export const translations = {
  en: {
    // Password protection translations
    accessRequired: "Access Required",
    accessDescription: "Want full access? Contact us, we'll be happy to help you further!",
    enterPassword: "Enter password",
    unlockLesson: "Unlock Lesson",
    noPassword: "No password yet?",
    contactUs: "Contact us",
    forAccess: "for access",

    // Navigation translations
    home: "Home",
    back: "Back",
    next: "Next",
    restart: "Restart",

    // General translations
    loading: "Loading...",
    error: "Error",
    success: "Success!",
  },
  nl: {
    // Password protection translations
    accessRequired: "Toegang Vereist",
    accessDescription: "Wil je volledige toegang? Neem contact op, dan helpen we je graag verder!",
    enterPassword: "Voer wachtwoord in",
    unlockLesson: "Les Ontgrendelen",
    noPassword: "Nog geen wachtwoord?",
    contactUs: "Neem contact op",
    forAccess: "voor toegang",

    // Navigation translations
    home: "Home",
    back: "Terug",
    next: "Volgende",
    restart: "Herstart",

    // General translations
    loading: "Laden...",
    error: "Fout",
    success: "Succes!",
  },
}

// The provider component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language state from localStorage or browser settings
// LanguageContext.tsx
const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
  const savedLanguage = localStorage.getItem("preferredLanguage")

  // Als iemand al gekozen heeft: respecteer dat
  if (savedLanguage && Object.values(LANGUAGES).some((lang) => lang.code === savedLanguage)) {
    return Object.values(LANGUAGES).find((lang) => lang.code === savedLanguage) || LANGUAGES.NL
  }

  // Anders: ALTIJD Nederlands als default (browser negeren)
  return LANGUAGES.NL
})


  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem("preferredLanguage", currentLanguage.code)
  }, [currentLanguage])

  // Function to change language
  const changeLanguage = (langCode: string) => {
    const newLanguage = Object.values(LANGUAGES).find((lang) => lang.code === langCode)

    if (newLanguage) {
      setCurrentLanguage(newLanguage)
    }
  }

  // Value to be provided to consumers
  const contextValue: LanguageContextType = {
    language: currentLanguage,
    changeLanguage,
    languages: LANGUAGES,
    translations: translations,
  }

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
}
